import PatternHttpAPI from 'frontcore/presentation/perimeter-finder/data/PatternHttpAPI'
import PerimeterHttpAPI from 'frontcore/presentation/perimeter-finder/data/PerimeterHttpAPI'
import useAllowedPerimetersStore from 'frontcore/store/useAllowedPerimetersStore'
import { computed, ComputedRef, reactive, readonly, ref, Ref } from 'vue'
import { defineStore } from 'pinia'
import { FALLBACK_LANGUAGE, getLanguage } from 'frontcore/i18n'
import { Nullable } from 'frontcore/presentation/types'
import { Pattern, Perimeter, PerimeterAPIResponse, PerimeterFactoryLevel } from 'frontcore/presentation/perimeter-finder/domain/types'
import { PROMISE_STATUS } from 'frontcore/constants/promises'
import { Proxy } from 'frontcore/constants/proxy'
import { StoreRequestStatus, StoreWithRequest } from 'frontcore/store/types'
import { useProfileStore } from 'frontcore/store/useProfileStore'

export class PerimeterRequestError extends Error {}

interface IPerimeterState {
  allPerimeters: {
    data: Perimeter[]
    promise: Promise<PerimeterAPIResponse[]>
    promiseStatus: PROMISE_STATUS
  }
  perimetersAllowed: Perimeter[]
  perimetersAllowedIds: Readonly<number[]>
  patterns: Proxy<Pattern[]>
  factoryLevels: PerimeterFactoryLevel[]
  isPerimeterFinderOpen: boolean
  selectedPerimeterIds: PerimeterFactoryLevel['id'][]
}

interface IPerimeterStore extends StoreWithRequest {
  areAllPerimetersSelected: ComputedRef<boolean>
  getAllowedPerimeters: ComputedRef<Perimeter[]>
  getAllPerimeters: ComputedRef<Perimeter[]>
  getFactoryLevels: ComputedRef<PerimeterFactoryLevel[]>
  getPatterns: ComputedRef<Pattern[]>
  lastSelectedPerimeterId: ComputedRef<number | undefined>
  perimetersAllowedIds: ComputedRef<Readonly<number[]>>
  selectedPerimeterIds: ComputedRef<number[]>
  selectedPerimeterPath: ComputedRef<string[]>
  state: IPerimeterState
  addPerimeter: (perimeter: Perimeter) => void
  addPerimeterInFactoryLevel: (perimeter: Perimeter) => void
  clearSelectedPerimeterIds: () => void
  fetchPatterns: () => Promise<void>
  fetchPerimeters: () => Promise<void>
  getChildPatternByParent: (patternId: number) => Pattern | undefined
  getChildPatternsByParent: (parentId: number) => Pattern[]
  getChildrenByPerimeterId: (id: number) => Perimeter[]
  getFactoryLevelsHierarchyByCaller: (caller: string) => PerimeterFactoryLevel[]
  getFactoryLevelByPatternId: (patternId: number, factoryLevels?: PerimeterFactoryLevel[]) => PerimeterFactoryLevel | undefined
  getPatternById:(patternId: number) => Pattern | undefined
  getPatternLevelById: (patternId: number) => number
  getPerimeterById: (perimeterId: Nullable<number>) => Perimeter | undefined
  getPerimeterParentsById: (perimeterId: Nullable<number>) => Perimeter[]
  getPerimeterPathById: (perimeterId: Nullable<number>) => string[]
  getPerimetersByPattern: (pattern?: Pattern) => Perimeter[],
  getRecursiveChildrenForPerimeter: (perimeter: Perimeter, recursiveChildren?: Perimeter[]) => Perimeter[]
  getTopLevelAllowedPerimeterIds: (caller: string) => number[]
  getTopLevel: (caller: string) => PerimeterFactoryLevel | undefined
  getChildrenPerimetersThatAreAmongAllowedPerimeters: (perimterId: number, allowedPerimeterIds: number[]) => number[]
  selectPerimeterIds: (ids: IPerimeterState['selectedPerimeterIds']) => void
  setIsPerimeterFinderOpen: (isPerimeterFinderOpen: boolean) => void
  setPermissionInPerimeters: () => void,
  updatePerimeter: (perimeter: Perimeter) => void
  updatePerimeterInFactoryLevel: (perimeter: Perimeter) => void
  areAllPerimetersLoaded: ComputedRef<boolean>,
  arePatternsLoaded: ComputedRef<boolean>
}

export const usePerimeterStore = defineStore('perimeter', (): IPerimeterStore => {
  const state = reactive<IPerimeterState>({
    allPerimeters: {
      data: [] as Perimeter[],
      promise: Promise.resolve([] as PerimeterAPIResponse[]),
      promiseStatus: PROMISE_STATUS.notStarted,
    },
    perimetersAllowed: [],
    perimetersAllowedIds: [],
    patterns: {
      data: [],
      promise: Promise.resolve([]),
      promiseStatus: PROMISE_STATUS.notStarted,
    },
    factoryLevels: [],
    isPerimeterFinderOpen: false,
    selectedPerimeterIds: [],
  })

  const status: Ref<StoreRequestStatus> = ref(StoreRequestStatus.idle)

  const getAllPerimeters = computed(() => state.allPerimeters.data ?? [])
  const getPatterns = computed(() => state.patterns.data ?? [])
  const getAllowedPerimeters = computed(() => state.perimetersAllowed)
  const selectedPerimeterIds = computed(() => state.selectedPerimeterIds)
  const selectedPerimeterPath = computed<string[]>(() => {
    if (lastSelectedPerimeterId.value === undefined) { return [] }
    return getPerimeterPathById(lastSelectedPerimeterId.value)
  })
  const lastSelectedPerimeterId = computed(() => state.selectedPerimeterIds.at(-1))
  const areAllPerimetersSelected = computed(() => state.selectedPerimeterIds.length === 0)
  const getFactoryLevels = computed(() => state.factoryLevels)
  const perimetersAllowedIds = computed(() => state.perimetersAllowedIds)

  const getTopLevelAllowedPerimeterIds = (caller: string) => {
    return getTopLevel(caller)?.filteredPerimeters.filter(
      (perimeter) => state.perimetersAllowedIds.includes(perimeter.id)).map((perimeter) => perimeter.id) ?? []
  }

  const getTopLevel = (caller: string) => {
    return getFactoryLevelsHierarchyByCaller(caller).find((factoryLevel) => factoryLevel?.parent == null)
  }

  const setPerimetersAllowed = async () => {
    const allowedPerimetersStore = useAllowedPerimetersStore()
    await allowedPerimetersStore.fetch()
    state.perimetersAllowedIds = allowedPerimetersStore.allowedIds
  }

  const fetchPerimeters = async ({ force } = { force: false }) => {
    const httpAPI = new PerimeterHttpAPI()
    await setPerimetersAllowed()
    
    if (state.allPerimeters.promiseStatus == PROMISE_STATUS.notStarted || force) {
      state.allPerimeters.promise = httpAPI.request()
      state.allPerimeters.promiseStatus = PROMISE_STATUS.pending
    }

    try {
      if (force || getAllPerimeters.value.length === 0) {
        const apiResponse = await state.allPerimeters.promise
        state.allPerimeters.data = httpAPI.transformToPerimeters(apiResponse)
        state.allPerimeters.promiseStatus = PROMISE_STATUS.complete
      }
      state.perimetersAllowed = getAllPerimeters.value.filter(
        perimeter => state.perimetersAllowedIds.includes(perimeter.id)
      )
    } catch {
      state.allPerimeters.promiseStatus = PROMISE_STATUS.notStarted
    }
  }

  const _sortPatterns = (patterns: Pattern[]) => {
    const sortedPatterns: Pattern[] = []

    const addPatternsWithParent = (parentId: number | null) => {
      const children = patterns.filter((item: Pattern) => item.parent === parentId)
      children.forEach((child: Pattern) => {
        sortedPatterns.push(child)
        addPatternsWithParent(child.id)
      })
    }

    addPatternsWithParent(null)
    return sortedPatterns
  }

  const fetchPatterns = async ({ force } = { force: false }) => {
    if (state.patterns.promiseStatus == PROMISE_STATUS.notStarted || force) {
      const httpAPI = new PatternHttpAPI()
      state.patterns.promise = httpAPI.request()
      state.patterns.promiseStatus = PROMISE_STATUS.pending
    }
    try {
      if (force || getPatterns.value.length === 0) {
        const patterns = await state.patterns.promise
        state.patterns.data = _sortPatterns(patterns)
      }
      state.patterns.promiseStatus = PROMISE_STATUS.complete
    } catch (e) {
      state.patterns.promiseStatus = PROMISE_STATUS.notStarted
    }
  }
  const fetch = async ({ force } = { force: false }) => {
    status.value = StoreRequestStatus.loading
    await fetchPatterns({force})
    await fetchPerimeters({force})
    if (state.allPerimeters.promiseStatus === PROMISE_STATUS.notStarted || state.patterns.promiseStatus === PROMISE_STATUS.notStarted) {
      status.value = StoreRequestStatus.failed
      throw new PerimeterRequestError()
    }
    const lang = getLanguage()
    const factoryLevels: PerimeterFactoryLevel[] = []
    for (const pattern of getPatterns.value) {
      const perimeters: Perimeter[] = state.perimetersAllowed.filter(({ pattern: patternId }) => patternId === pattern.id)
      const factoryLevel: PerimeterFactoryLevel = {
        id: pattern.id,
        callers: pattern.callers,
        filteredPerimeters: perimeters,
        name: pattern.localized[lang] ?? pattern.localized[FALLBACK_LANGUAGE] ?? '-',
        parent: factoryLevels.find(({ id }) => id === pattern.parent) ?? null,
        perimeters,
      }
      factoryLevels.push(factoryLevel)
    }
    state.factoryLevels = factoryLevels
    status.value = StoreRequestStatus.succeeded
  }

  const getChildrenPerimetersThatAreAmongAllowedPerimeters = (perimeterId: number, allowedPerimterIds: number[]) => {
    const perimeter = getPerimeterById(perimeterId)
    const childPerimeterIds: number[] = []
    if (perimeter) {
      const checkpointChildPerimeter: Perimeter[] = []
      getRecursiveChildrenForPerimeter(perimeter, checkpointChildPerimeter)
      checkpointChildPerimeter.forEach((_perimeterChildren) => {
        if (allowedPerimterIds.includes(_perimeterChildren.id)) {
          childPerimeterIds.push(_perimeterChildren.id)
        }
      })
    }
    return childPerimeterIds
  }

  const selectPerimeterIds = (ids: IPerimeterState['selectedPerimeterIds']) => {
    state.selectedPerimeterIds = ids
  }

  const clearSelectedPerimeterIds = () => {
    state.selectedPerimeterIds = []
  }

  const setIsPerimeterFinderOpen = (isPerimeterFinderOpen: boolean) => {
    state.isPerimeterFinderOpen = isPerimeterFinderOpen
  }

  const setPermissionInPerimeters = () => {
    const profileStore = useProfileStore()
    for (const perimeter of getAllPerimeters.value) {
      perimeter.userHasPermission = profileStore.hasPermissionInPerimeter(perimeter.id)
    }
  }

  const getPatternById = (patternId: number) => {
    return getPatterns.value.find(pattern => pattern.id === patternId)
  }
  
  const getPatternLevelById = (patternId: number) => {
    let level = 0
    let currentPattern: Pattern | undefined = getPatternById(patternId)
    while (currentPattern?.parent) {
      level++
      currentPattern = getPatternById(currentPattern.parent)
      if (!currentPattern) {
        break
      }
    }
    return level
  }
  const getChildPatternByParent = (patternId: number) => {
    return getPatterns.value.find(pattern => pattern.parent === patternId)
  }
  
  const getChildPatternsByParent = (parentId: number) => {
    return getPatterns.value.filter(pattern => pattern.parent === parentId)
  }

  const getPerimetersByPattern = (pattern?: Pattern) => {
    return pattern ? getAllPerimeters.value.filter(perimeter => perimeter.pattern === pattern.id) : []
  }

  const getPerimeterById = (perimeterId: Nullable<number>) => {
    if (perimeterId === null) return undefined
    return getAllPerimeters.value.find(perimeter => perimeter.id === perimeterId)
  }

  const getChildrenByPerimeterId = (id: number) => {
    return getAllPerimeters.value.filter(perimeter => perimeter.parent === id)
  }
  
  const getRecursiveChildrenForPerimeter = (perimeter: Perimeter, recursiveChildren: Perimeter[] = []) => {
    getChildrenByPerimeterId(perimeter.id).forEach(children => {
      recursiveChildren.push(children)
      if (getChildrenByPerimeterId(children.id).length > 0) {
        recursiveChildren = getRecursiveChildrenForPerimeter(
          children, recursiveChildren
        )
      }
    })
    return recursiveChildren
  }
  const getPerimeterParentsById = (perimeterId: Nullable<number>): Perimeter[] => {
    const tracedPerimeters: Perimeter[] = []
    let selectedPerimeter = getAllPerimeters.value.find((p) => { return p.id === perimeterId })
    while (selectedPerimeter !== undefined) {
      const perimeter = selectedPerimeter
      tracedPerimeters.unshift(perimeter)
      selectedPerimeter = getAllPerimeters.value.find((p) => { return p.id === selectedPerimeter?.parent })
    }
    return tracedPerimeters
  }

  const getPerimeterPathById = (perimeterId: Nullable<number>): string[] => {
    return getPerimeterParentsById(perimeterId).map(perimeter => perimeter.name)
  }

  const getFactoryLevelsHierarchyByCaller = (caller: string) => {
    const matchingFactoryLevels = state.factoryLevels.filter(factoryLevel => factoryLevel.callers.includes(caller))
    const factoryLevelHierarchy: PerimeterFactoryLevel[] = []

    for (const factoryLevel of matchingFactoryLevels) {
      factoryLevelHierarchy.unshift(factoryLevel)
      let currentFactoryLevel = factoryLevel
      while (currentFactoryLevel.parent) {
        const upperFactoryLevel = state.factoryLevels.find(level => level.id === currentFactoryLevel.parent?.id)
        if (upperFactoryLevel) {
          factoryLevelHierarchy.unshift(upperFactoryLevel)
          currentFactoryLevel = upperFactoryLevel
        } else {
          break
        }
      }
    }
    return factoryLevelHierarchy
  }

  const getFactoryLevelByPatternId = (patternId: number, factoryLevels?: PerimeterFactoryLevel[]) => {
    const levels = factoryLevels || state.factoryLevels
    return levels.find(level => level.id === patternId)
  }

  const areAllPerimetersLoaded = computed(() => {
    return state.allPerimeters.promiseStatus === PROMISE_STATUS.complete
  })
  const arePatternsLoaded = computed(() => {
    return state.patterns.promiseStatus === PROMISE_STATUS.complete
  })

  const updatePerimeter = (perimeter: Perimeter) => {
    const index = state.allPerimeters.data.findIndex(p => p.id === perimeter.id)
    if (index !== -1) {
      state.allPerimeters.data[index] = perimeter
    }
  }

  const updatePerimeterInFactoryLevel = (perimeter: Perimeter) => {
    const factoryLevel = state.factoryLevels.find(level => level.id === perimeter.pattern)
    if (factoryLevel) {
      const index = factoryLevel.filteredPerimeters.findIndex(p => p.id === perimeter.id)
      if (index !== -1) {
        factoryLevel.filteredPerimeters[index] = perimeter
      }
    }
  }

  const addPerimeter = (perimeter: Perimeter) => {
    state.allPerimeters.data.push(perimeter)
  }

  const addPerimeterInFactoryLevel = (perimeter: Perimeter) => {
    const factoryLevel = state.factoryLevels.find(level => level.id === perimeter.pattern)
    if (factoryLevel) {
      factoryLevel.filteredPerimeters.push(perimeter)
    }
  }


  return {
    areAllPerimetersSelected,
    clearSelectedPerimeterIds,
    fetch,
    fetchPatterns,
    fetchPerimeters,
    getAllowedPerimeters,
    getAllPerimeters,
    getChildPatternByParent,
    getChildPatternsByParent,
    getChildrenByPerimeterId,
    getFactoryLevels,
    getFactoryLevelsHierarchyByCaller,
    getFactoryLevelByPatternId,
    getPatternLevelById,
    getPatterns,
    getPatternById,
    getPerimeterById,
    getPerimeterParentsById,
    getPerimeterPathById,
    getPerimetersByPattern,
    getRecursiveChildrenForPerimeter,
    getTopLevel,
    getTopLevelAllowedPerimeterIds,
    getChildrenPerimetersThatAreAmongAllowedPerimeters,
    lastSelectedPerimeterId,
    perimetersAllowedIds,
    selectedPerimeterPath,
    selectPerimeterIds,
    setIsPerimeterFinderOpen,
    setPermissionInPerimeters,
    selectedPerimeterIds,
    areAllPerimetersLoaded,
    arePatternsLoaded,
    state,
    status: readonly(status),
    updatePerimeter,
    updatePerimeterInFactoryLevel,
    addPerimeter,
    addPerimeterInFactoryLevel,
  }
})
